import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_PHOTO_CAMERA, photoCameraConstraints } from './photoCamera';
import { isNativePhotoCamera, openNativePhotoCamera } from './nativePhotoCamera';

export const CAMERA_READY_TIMEOUT_MS = 15000;

// 브라우저/메모리 UVC 스트림을 기존 video에 연결한다. 파일 저장·서버 전송 없음.
export default function usePhotoCamera(videoRef, camera = DEFAULT_PHOTO_CAMERA, attempt = 0, onError) {
  const { mode, deviceId } = camera;
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const active = useRef(null);
  const errorCallback = useRef(onError);
  errorCallback.current = onError;
  const stop = useCallback(() => active.current?.stop(), []);
  useEffect(() => {
    let closed = false;
    let stream;
    let nativeSession;
    let timeout;
    const video = videoRef.current;
    setReady(false);
    setError(null);
    const stopSession = () => {
      closed = true;
      clearTimeout(timeout);
      nativeSession?.stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      video?.removeEventListener('playing', onPlaying);
      stream?.getTracks().forEach(t => { t.removeEventListener?.('ended', onEnded); t.removeEventListener?.('mute', onEnded); t.stop(); });
      if (video && video.srcObject === stream) video.srcObject = null;
    };
    const fail = err => {
      if (closed) return;
      stopSession();
      setReady(false);
      setError(err);
      errorCallback.current?.(err);
    };
    function onEnded() { fail(new Error('CAMERA_DISCONNECTED')); }
    function onVisibilityChange() { if (document.hidden) onEnded(); }
    function onPlaying() {
      if (closed || !stream || video.videoWidth <= 0 || video.videoHeight <= 0 || video.readyState < 2) return;
      clearTimeout(timeout);
      setReady(true);
    }
    active.current = { stop: stopSession };
    video?.addEventListener('playing', onPlaying);
    timeout = setTimeout(() => fail(new Error('CAMERA_TIMEOUT')), CAMERA_READY_TIMEOUT_MS);
    (async () => {
      try {
        let opened;
        if (isNativePhotoCamera({ mode, deviceId })) {
          document.addEventListener('visibilitychange', onVisibilityChange);
          nativeSession = openNativePhotoCamera(deviceId, fail);
          opened = await nativeSession.ready;
        } else {
          if (!navigator.mediaDevices?.getUserMedia) throw new Error('NO_MEDIA_DEVICES');
          opened = await navigator.mediaDevices.getUserMedia(photoCameraConstraints({ mode, deviceId }));
        }
        if (closed) { opened.getTracks().forEach(t => t.stop()); return; }
        stream = opened;
        const track = stream.getVideoTracks?.()[0] || stream.getTracks()[0];
        if (!track || track.readyState === 'ended') throw new Error('CAMERA_DISCONNECTED');
        track.addEventListener?.('ended', onEnded);
        track.addEventListener?.('mute', onEnded);
        if (video) video.srcObject = stream;
      } catch (err) { fail(err); }
    })();
    return stopSession;
  }, [mode, deviceId, attempt, videoRef]);
  return { ready, error, stop };
}
